import { getFRCLabels } from '../../../../shared/util/FrcDistribution';
import { RoadLength } from '../../../menu/right-menu/layers-menu/commute-and-mobility/commute-and-mobility.service';

/** Motorways and trunk roads with their slip roads. */
const HIGHWAY_MAX_FRC = 1;

/** FRC 7+ are driveways, alleys and parking aisles - excluded from averages. */
const PUBLIC_MAX_FRC = 6;

export interface TrafficSummary {
  /** Vehicles a day on an average mile of public road in the cell. */
  overall: number;
  /** Same, off highways. Null when there is nothing else. */
  local: number | null;
  /** Same, on highways. Null when none cross the cell. */
  highway: number | null;
}

/**
 * Length-weighted AADT across road classes ("traffic on an average mile of
 * road here"). A plain mean over classes would let a short slip road count as
 * much as every residential street.
 */
export function summariseTraffic(
  aadtByClass: RoadLength,
  lengthByClass: RoadLength
): TrafficSummary | null {
  const isHighway = (roadClass: string) =>
    getFRCLabels(roadClass) <= HIGHWAY_MAX_FRC;
  const isPublic = (roadClass: string) =>
    getFRCLabels(roadClass) <= PUBLIC_MAX_FRC;

  const weighted = (include: (roadClass: string) => boolean): number | null => {
    let vehicleMiles = 0;
    let length = 0;

    for (const [roadClass, aadt] of Object.entries(aadtByClass)) {
      const roadLength = lengthByClass[roadClass as keyof RoadLength];
      if (typeof aadt !== 'number' || typeof roadLength !== 'number') continue;
      if (!include(roadClass)) continue;

      vehicleMiles += aadt * roadLength;
      length += roadLength;
    }

    return length > 0 ? vehicleMiles / length : null;
  };

  const overall = weighted(isPublic);
  if (overall === null) return null;

  return {
    overall,
    local: weighted(
      (roadClass) => isPublic(roadClass) && !isHighway(roadClass)
    ),
    highway: weighted(isHighway),
  };
}

/** Modelled counts, so rounded to the precision they can carry. */
export function formatTrafficCount(value: number): string {
  const step =
    value >= 10000 ? 1000 : value >= 1000 ? 100 : value >= 100 ? 10 : 1;

  return (Math.round(value / step) * step).toLocaleString('en-US');
}
